/**
 * vatReturn — reine UStVA-Vorschau-Aggregation (§18 UStG), M8.
 *
 * Stellt die geschuldete USt (Output aus Ausgangsrechnungen + §13b-Eingangsschuld +
 * innergem. Erwerb) der abziehbaren Vorsteuer gegenüber und berechnet die Zahllast
 * (KZ 83). Reine Funktion — KEINE Firebase-/React-Imports, nur Typen (compile-time
 * erased) + die Perioden-Helfer aus `expenseSummary`. Trivial unit-testbar.
 *
 * Disziplin (golden rules):
 *   - Integer-Cents durchgängig, vorzeichen-aware (Gutschrift/Storno negativ).
 *   - `buildVatReturn` ist TOTAL: keine Exceptions. Unklassifizierbares wandert in den
 *     `unclassifiedIncome`-Bucket — NIE geraten, NIE still nach KZ 81/86 (§3.5).
 *   - §13b/i.g.Erwerb werden NIE saldiert: geschuldete USt (KZ 84/85, 89/93) UND
 *     Vorsteuer (KZ 66/67/61) getrennt gefüllt (§4.2).
 *   - §19 (smallBusiness): alle Ausgangs-/VSt-KZ = 0; nur §13b/Erwerb-Pflicht-KZ
 *     gefüllt; `smallBusinessHasReverseChargeDuty` markiert die Pflicht-Meldung (§5.4).
 *
 * Mapping VB-1-Auswahl (BT-151) × Satz → UStVA-KZ siehe §4 architecture-m8.md.
 *
 * @module vatReturn
 */

import { SummaryPeriod, isWithinPeriod } from './expenseSummary';
import { InvoiceTaxCategoryKey, Bt151CategoryCode } from './taxCategoryCatalog';
import type { Expense, TaxScheme } from '../types/expenseTypes';

// ─── Types (§3.1) ────────────────────────────────────────────────────────────────

export type TaxationMode = 'soll' | 'ist';

/** Eine Umsatz-Zeile zu genau (Steuersatz × VB-1-Kategorie). Aus dem Per-Satz-Reader (§3.4). */
export interface IncomeRateLine {
  /** USt-Satz in Prozent (19 | 7 | 0 | 16/5 → Bucket). */
  ratePercent: number;
  /** Netto-Bemessungsgrundlage in Integer-Cents (vorzeichen-aware; Gutschrift negativ). */
  netCents: number;
  /** USt-Betrag in Integer-Cents (vorzeichen-aware). */
  vatCents: number;
  /** Persistierte VB-1-Auswahl (verlustfrei; löst E-Mehrdeutigkeit). null ⇒ Altbestand. */
  selection: InvoiceTaxCategoryKey | null;
  /** BT-151-Code (abgeleitet/persistiert). Diagnostik / Altbestands-Fallback. */
  categoryCode: Bt151CategoryCode | null;
}

/** Normalisierte Einnahme (entkoppelt von der Firestore-Doc-Form). Mapper in der Page erzeugt sie. */
export interface VatIncomeEntry {
  /** invoiceId. */
  id: string;
  /** Soll-Periodenkriterium: BT-72 deliveryDate; null ⇒ Fallback invoiceDate (unsicher-Flag). */
  serviceDateISO: string | null;
  /** Rechnungsdatum (BT-2), ISO YYYY-MM-DD. Fallback-Periodenkriterium. */
  invoiceDateISO: string;
  /** true ⇒ serviceDate fehlte, invoiceDate als unsicherer Fallback genutzt (UI-Hinweis). */
  periodDateIsFallback: boolean;
  /** Per-Satz/Kategorie-Zeilen aus dem gemeinsamen Reader (`invoicePerRate`). Bei Storno-Reversal bereits × −1 negiert (§3.7). */
  rateLines: IncomeRateLine[];
  /** Brutto-Gesamtbetrag in Integer-Cents (vorzeichen-aware). Bei Storno-Reversal negativ (§3.7). Basis der Ist-Brutto-Quote. */
  grossCents: number;
  /** true ⇒ Anzahlungs-/Abschlagsrechnung (Mindest-Ist auch bei Soll, §13(1)1a S.4). */
  isDownPayment: boolean;
  /** Käufer-USt-IdNr. vorhanden? (IGL KZ 41 nur dann; sonst Bucket). */
  hasBuyerVatId: boolean;
  /**
   * true ⇒ dieses Dokument ist ein Storno-Dokument (nicht die stornierte Original-Rechnung).
   * Diagnostisch; das Netting kommt aus den bereits negierten rateLines/grossCents (§3.7 rev.3).
   */
  isCancellation?: boolean;
  /**
   * true ⇒ Storno-Reversal: rateLines und grossCents sind bereits × −1 negiert (Mapper, §3.7).
   * `buildVatReturn` summiert storno-neutral — der Netting-Effekt entsteht aus den Vorzeichen.
   * Rein diagnostisch; nie als Filter innerhalb dieser Lib auswerten.
   */
  isCancellationReversal?: boolean;
  /** Ist-Modus / Anzahlung: in der Periode vereinnahmter Brutto-Anteil (anteilige USt, OP-1). */
  paidInPeriodGrossCents?: number;
}

/** Eingangsseite — aus der bestehenden Ausgaben-Aggregation (Cockpit/Adapter §3.3). */
export interface VatInputAggregate {
  /** → KZ 66 (Vorsteuer §15 Abs.1 Nr.1). */
  vatDeductibleStandardCents: number;
  /** §13b Eingang geschuldet → KZ 84/85 (bzw. 46/47). */
  reverseChargeOwedCents: number;
  /** Netto-Basis dazu. */
  reverseChargeNetCents: number;
  /** → KZ 67 (VSt aus §13b). */
  reverseChargeDeductibleCents: number;
  /** Innergem. Erwerb Steuer → KZ 89/93. */
  intraCommunityAcqVatCents: number;
  /** Netto-Basis dazu. */
  intraCommunityAcqNetCents: number;
  /** → KZ 61 (VSt aus i.g. Erwerb). */
  intraCommunityAcqDeductibleCents: number;
  /** A1-Übernahme: draft-Belege mit realer §13b/IGL-Schuld (sichtbarer Hinweis). */
  excludedDraftVatOwedCount: number;
  /**
   * VETO-1 (§7.4) — eingangsseitiger Transparenz-/fail-closed-Zähler: gebuchte
   * Ausgaben, deren `taxScheme` im Eingangs-Switch NICHT zu den UStVA-Summen
   * beiträgt (mind. `import_third_country`, `needs_review`; Details Zähl-Partition
   * §7.4). Ohne diesen Zähler verlöre ein KMU mit Drittland-Import still die
   * gezahlte EUSt → Kz83 systematisch zu hoch. Vorzeichen-aware Summe der
   * `grossCents` gezählter Ausgaben (Storno-neutral).
   *
   * PFLICHT (nicht optional): der Compiler erzwingt jede Konstruktionsstelle —
   * ein `?? 0`-Default wäre ein stilles fail-open.
   */
  unclassifiedExpenseGrossCents: number;
  /** VETO-1 (§7.4) — Anzahl der gezählten, eingangsseitig unklassifizierten Ausgaben. */
  unclassifiedExpenseCount: number;
}

/** Ergebnis — ALLE Beträge Integer-Cents, vorzeichen-aware. KZ-benannt (precheck 🔴-4). */
export interface VatReturnFigures {
  period: SummaryPeriod;
  taxationMode: TaxationMode;
  /** §19 — Sonderdarstellung (§5.4). */
  smallBusiness: boolean;
  /** §19 MIT §13b/Erwerb-Pflicht (❌-3). */
  smallBusinessHasReverseChargeDuty: boolean;

  // Ausgang steuerpflichtig
  kz81BaseCents: number; kz81VatCents: number;  // 19 %
  kz86BaseCents: number; kz86VatCents: number;  // 7 %
  kz35BaseCents: number; kz36VatCents: number;  // andere Sätze inkl. §12(3)-PV (36 = 0 bei PV)
  // Ausgang steuerfrei / §13b-Ausgang
  kz41BaseCents: number;                        // i.g. Lieferung (mit VSt-Abzug)
  kz43BaseCents: number;                        // Ausfuhr/§4 Nr.1–7 (mit VSt-Abzug)
  kz48BaseCents: number;                        // §4 Nr.8–29 (ohne VSt-Abzug)
  kz60BaseCents: number;                        // §13b-Ausgang (Empfänger schuldet)
  // Eingang §13b / i.g. Erwerb (geschuldet)
  kz46BaseCents: number; kz47VatCents: number;  // §13b Abs.1 EU-Leistung
  kz84BaseCents: number; kz85VatCents: number;  // §13b Abs.2
  kz89BaseCents: number; kz89VatCents: number;  // i.g. Erwerb 19 %
  kz93BaseCents: number; kz93VatCents: number;  // i.g. Erwerb 7 %
  // Vorsteuer
  kz66VatCents: number;                         // VSt aus Rechnungen
  kz61VatCents: number;                         // VSt aus i.g. Erwerb
  kz67VatCents: number;                         // VSt aus §13b
  // Ergebnis
  kz83PayableCents: number;                     // Zahllast (>0) / Überschuss (<0)

  // Transparenz / fail-closed
  unclassifiedIncome: { grossCents: number; count: number };  // Altbestand/uneindeutig (§3.5)
  /**
   * VETO-1 (§7.4) — eingangsseitiger Bucket, symmetrisch zu `unclassifiedIncome`:
   * gebuchte Ausgaben mit einem im Eingangs-Switch unbehandelten `taxScheme`
   * (mind. Drittland-EUSt, `needs_review`). Wird durchgereicht aus dem Aggregat und
   * — anders als `unclassifiedIncome` — im §19-Pfad NICHT genullt (die Eingangs-
   * Iteration läuft dort ebenfalls; Export ist für §19 über B3 geblockt).
   */
  unclassifiedExpense: { grossCents: number; count: number };
  excludedDraftVatOwedCount: number;
  includedIncomeCount: number;
}

/**
 * Reservierter Erweiterungspunkt — stabile Signatur (storno-neutral seit §3.7 rev.3).
 * `excludeCancelled` entfernt: Storno-Docs kommen mit bereits negierten rateLines/grossCents
 * vom Mapper; `buildVatReturn` filtert kein `isCancellation` mehr (kein Storno-Branch).
 */
export interface BuildVatReturnOptions {
  // (Leer — zukünftige Erweiterungen)
}

// ─── Klassifikation einer Einnahme-Zeile → KZ (§4.1 / §3.5 fail-closed) ──────────

/**
 * Ziel-Kennzahl einer Einnahme-Zeile. `bucket` ⇒ unklassifiziert (NIE 81/86 geraten).
 * `none` ⇒ kein UStVA-Eintrag (nur in §19-Sonderpfad relevant; hier nicht erreicht).
 */
type IncomeTarget =
  | 'kz81'   // standard 19 %
  | 'kz86'   // standard 7 %
  | 'kz35'   // andere Sätze inkl. §12(3)-PV (Basis 35, USt 36)
  | 'kz41'   // i.g. Lieferung (USt-IdNr. vorausgesetzt)
  | 'kz43'   // Ausfuhr / §4 Nr.1–7
  | 'kz48'   // §4 Nr.8–29
  | 'kz60'   // §13b-Ausgang
  | 'bucket';

/**
 * Bildet eine Einnahme-Zeile fail-closed auf eine UStVA-Kennzahl ab (§4.1).
 * Persistierte `selection` (verlustfrei) hat Vorrang; null-Altbestand wird nur für
 * eindeutiges 19/7-Standardgeschäft automatisch klassifiziert, sonst Bucket (§3.5).
 *
 * @param line          Einnahme-Zeile (Satz + VB-1-Auswahl/Kategorie).
 * @param hasBuyerVatId Käufer-USt-IdNr. vorhanden? (Gate für IGL → KZ 41).
 * @returns Ziel-Kennzahl oder `bucket`.
 */
function resolveIncomeTarget(line: IncomeRateLine, hasBuyerVatId: boolean): IncomeTarget {
  const sel = line.selection;
  const rate = line.ratePercent;

  // 1. Explizite VB-1-Auswahl (verlustfrei) — Vorrang.
  switch (sel) {
    case 'standard':
      if (rate === 19) return 'kz81';
      if (rate === 7) return 'kz86';
      return 'bucket'; // 16/5 % o. ä. — nicht MVP-safe
    case 'pv_12_3':
      return 'kz35'; // §12(3)-PV: Basis 35, USt 36 (=0)
    case 'intra_community_6a':
      return hasBuyerVatId ? 'kz41' : 'bucket'; // KZ 41 setzt USt-IdNr. voraus
    case 'reverse_charge_13b':
      return 'kz60';
    case 'export_third_country':
      return 'kz43';
    case 'exempt_4':
      // §4 Nr.1–7 (43) vs. Nr.8–29 (48) ohne §4-Nr. nicht entscheidbar → Bucket (OP-3, fail-closed).
      return 'bucket';
    case 'small_business_19':
      // §19 — eigener Pfad; sollte hier nicht erreicht werden. Fail-closed.
      return 'bucket';
    case null:
      // 2. Altbestand: nur eindeutiges 19/7-Standardgeschäft automatisch klassifizieren.
      {
        const standardish = line.categoryCode === 'S' || line.categoryCode === null;
        if (standardish && rate === 19) return 'kz81';
        if (standardish && rate === 7) return 'kz86';
        return 'bucket';
      }
    default:
      return 'bucket'; // unbekannter Auswahl-String (defensiv)
  }
}

// ─── Perioden-Erkennung (Soll/Ist, §2.3) ─────────────────────────────────────────

/**
 * Anteil einer Einnahme, der in der Periode zu erfassen ist.
 * @returns null ⇒ nicht in der Periode (überspringen); sonst Bruchteil (1 = voll).
 *
 * Soll (Regelfall): Leistungsdatum BT-72, Fallback Rechnungsdatum (unsicher).
 * Ist / Anzahlung: Vereinnahmung (paidInPeriodGrossCents) als Brutto-Quote (OP-1).
 */
function recognizedFraction(
  entry: VatIncomeEntry,
  period: SummaryPeriod,
  mode: TaxationMode,
): number | null {
  // Ist-Versteuerung ODER Anzahlung (Mindest-Ist §13(1)1a S.4 auch beim Soll-Versteuerer)
  // ⇒ Erfassung im Vereinnahmungs-VAZ.
  const usePayment = mode === 'ist' || entry.isDownPayment;
  if (usePayment) {
    const paid = entry.paidInPeriodGrossCents ?? 0;
    if (paid === 0) return null;
    if (entry.grossCents === 0) return 1; // degeneriert (kein Brutto) — voll erfassen
    return paid / entry.grossCents;
  }

  // Soll, regulär: Leistungsdatum (BT-72), Fallback Rechnungsdatum.
  const periodDate = entry.serviceDateISO ?? entry.invoiceDateISO;
  return isWithinPeriod(periodDate, period) ? 1 : null;
}

/** Skaliert einen Integer-Cent-Betrag mit dem Erfassungsanteil → garantiert Integer. */
function scaleCents(cents: number, fraction: number): number {
  if (fraction === 1) return cents;
  return Math.round(cents * fraction);
}

// ─── Hauptfunktion ───────────────────────────────────────────────────────────────

/**
 * Reine Aggregation der UStVA-Vorschau.
 *
 * Total (wirft nie): leere/uneindeutige Eingaben liefern 0-Kennzahlen bzw. Bucket.
 * Alle Ergebnis-Cents sind per Konstruktion Integer (Integer-Arithmetik + Math.round).
 *
 * Storno-neutral (§3.7 rev.3): kein Storno-Filter hier — Storno-Docs kommen vom Mapper
 *   mit bereits × −1 negierten rateLines/grossCents (UmsatzsteuerPage). Das Netting entsteht
 *   durch die Vorzeichen, NICHT durch einen Flag-Filter in dieser Funktion.
 *
 * smallBusiness=true ⇒ nur §13b/Erwerb-relevante geschuldete KZ gefüllt (❌-3), alle
 *   Ausgangs-/VSt-KZ = 0; `smallBusinessHasReverseChargeDuty` markiert die Pflicht-Meldung.
 *
 * @param income       Normalisierte Einnahmen (Mapper erzeugt sie aus `invoices`; Storno bereits negiert).
 * @param input        Eingangs-Aggregat (Vorsteuer/§13b/Erwerb) — `toVatInputAggregate`.
 * @param period       Aufgelöster Zeitraum (resolvePeriod / currentQuarterPeriod).
 * @param taxationMode 'soll' (Default-Regelfall) | 'ist'.
 * @param smallBusiness §19-Status (sellerData.isSmallBusiness === true).
 * @param options      Reservierter Erweiterungspunkt (leer, storno-neutral seit §3.7).
 * @returns VatReturnFigures mit allen KZ + Transparenz-Buckets.
 */
export function buildVatReturn(
  income: VatIncomeEntry[],
  input: VatInputAggregate,
  period: SummaryPeriod,
  taxationMode: TaxationMode,
  smallBusiness: boolean,
  options?: BuildVatReturnOptions,
): VatReturnFigures {
  // Ergebnis-Akkumulatoren (alle Integer-Cents).
  let kz81BaseCents = 0, kz81VatCents = 0;
  let kz86BaseCents = 0, kz86VatCents = 0;
  let kz35BaseCents = 0, kz36VatCents = 0;
  let kz41BaseCents = 0;
  let kz43BaseCents = 0;
  let kz48BaseCents = 0;
  let kz60BaseCents = 0;
  let unclassifiedGrossCents = 0;
  let unclassifiedCount = 0;
  let includedIncomeCount = 0;

  // ── Ausgangsseite (eigene Umsätze) — im §19-Pfad KOMPLETT übersprungen (§5.4) ──
  if (!smallBusiness) {
    for (const entry of income ?? []) {
      const fraction = recognizedFraction(entry, period, taxationMode);
      if (fraction === null) continue; // nicht in der Periode

      includedIncomeCount += 1;
      let entryHadBucketLine = false;

      for (const line of entry.rateLines ?? []) {
        const net = scaleCents(line.netCents, fraction);
        const vat = scaleCents(line.vatCents, fraction);
        const target = resolveIncomeTarget(line, entry.hasBuyerVatId);

        switch (target) {
          case 'kz81': kz81BaseCents += net; kz81VatCents += vat; break;
          case 'kz86': kz86BaseCents += net; kz86VatCents += vat; break;
          case 'kz35': kz35BaseCents += net; kz36VatCents += vat; break; // PV: vat=0
          case 'kz41': kz41BaseCents += net; break;
          case 'kz43': kz43BaseCents += net; break;
          case 'kz48': kz48BaseCents += net; break;
          case 'kz60': kz60BaseCents += net; break;
          case 'bucket':
            unclassifiedGrossCents += net + vat;
            entryHadBucketLine = true;
            break;
          default:
            // Erschöpfender Switch — defensiv fail-closed in den Bucket.
            unclassifiedGrossCents += net + vat;
            entryHadBucketLine = true;
            break;
        }
      }

      if (entryHadBucketLine) unclassifiedCount += 1;
    }
  }

  // ── Eingangsseite (§13b / i.g. Erwerb / Vorsteuer) — getrennt, NIE saldiert (§4.2) ──
  // §13b-Eingang: geschuldete Steuer (KZ 84/85) UND Vorsteuer (KZ 67) beide gefüllt.
  // i.g. Erwerb:  Erwerbsteuer (KZ 89/93) UND Vorsteuer (KZ 61) beide gefüllt.
  // M8a-Vereinfachung (Changelog): das Aggregat trennt EU(46/47) nicht von Inland(84/85)
  // und 19/7 (89/93) nicht — alle Reverse-Charge-Eingänge → 84/85, alle Erwerbe → 89.
  const kz46BaseCents = 0;
  const kz47VatCents = 0;
  const kz84BaseCents = input.reverseChargeNetCents;
  const kz85VatCents = input.reverseChargeOwedCents;
  const kz89BaseCents = input.intraCommunityAcqNetCents;
  const kz89VatCents = input.intraCommunityAcqVatCents;
  const kz93BaseCents = 0;
  const kz93VatCents = 0;

  // Vorsteuer — im §19-Pfad IMMER 0 (kein Vorsteuerabzug, ❌-3).
  const kz66VatCents = smallBusiness ? 0 : input.vatDeductibleStandardCents;
  const kz61VatCents = smallBusiness ? 0 : input.intraCommunityAcqDeductibleCents;
  const kz67VatCents = smallBusiness ? 0 : input.reverseChargeDeductibleCents;

  // §19 mit §13b/Erwerb-Pflicht: geschuldete Steuer > 0 ⇒ reduzierte Pflicht-Meldung (§18 Abs.4a).
  const smallBusinessHasReverseChargeDuty =
    smallBusiness &&
    (input.reverseChargeOwedCents !== 0 || input.intraCommunityAcqVatCents !== 0);

  // ── Zahllast (KZ 83) = geschuldete USt − abziehbare Vorsteuer ──
  const owedVatCents =
    kz81VatCents + kz86VatCents + kz36VatCents + // Ausgang stpfl.
    kz85VatCents + kz47VatCents +                // §13b-Eingang geschuldet
    kz89VatCents + kz93VatCents;                 // i.g. Erwerb geschuldet
  const deductibleVatCents = kz66VatCents + kz61VatCents + kz67VatCents;
  const kz83PayableCents = owedVatCents - deductibleVatCents;

  return {
    period,
    taxationMode,
    smallBusiness,
    smallBusinessHasReverseChargeDuty,

    kz81BaseCents, kz81VatCents,
    kz86BaseCents, kz86VatCents,
    kz35BaseCents, kz36VatCents,
    kz41BaseCents,
    kz43BaseCents,
    kz48BaseCents,
    kz60BaseCents,
    kz46BaseCents, kz47VatCents,
    kz84BaseCents, kz85VatCents,
    kz89BaseCents, kz89VatCents,
    kz93BaseCents, kz93VatCents,
    kz66VatCents,
    kz61VatCents,
    kz67VatCents,
    kz83PayableCents,

    unclassifiedIncome: {
      grossCents: smallBusiness ? 0 : unclassifiedGrossCents,
      count: smallBusiness ? 0 : unclassifiedCount,
    },
    // VETO-1 (§7.4): 1:1 aus dem Aggregat — bewusst NICHT §19-genullt (die Eingangs-
    // Iteration in `toVatInputAggregate` läuft auch im §19-Pfad; Transparenz-Signal).
    unclassifiedExpense: {
      grossCents: input.unclassifiedExpenseGrossCents,
      count: input.unclassifiedExpenseCount,
    },
    excludedDraftVatOwedCount: input.excludedDraftVatOwedCount,
    includedIncomeCount: smallBusiness ? 0 : includedIncomeCount,
  };
}

// ─── Eingangs-Adapter (§3.3) — pure Ableitung aus geladenen Ausgaben ─────────────

/** §13b/IGL-Schemata, deren USt der Empfänger kraft Gesetzes schuldet (§13b/§1a UStG). */
const REVERSE_CHARGE_SCHEMES: ReadonlySet<TaxScheme> = new Set<TaxScheme>([
  'reverse_charge_13b',
  'intra_community_6a',
]);

/** Optionen für `toVatInputAggregate` — defaults fail-closed (spiegelt `summarizeExpenses`). */
export interface ToVatInputAggregateOptions {
  /** Default true: nur status==='booked' zählt in die Summen (Entwürfe = Vorschau). */
  bookedOnly?: boolean;
}

/**
 * Leitet das Eingangs-Aggregat (`VatInputAggregate`) rein aus den BEREITS geladenen
 * Ausgaben ab (keine zweite Firestore-Query, §3.3). Periodengefiltert über `expenseDate`.
 *
 * §13b-Eingang füllt BEIDE Seiten (geschuldet + Vorsteuer), nie saldiert — `expenseTax`
 * liefert `vatOwedCents` und `vatDeductibleCents` getrennt; `vatDeductibleCents` ist am
 * Quell-Feld bereits fail-closed (0, wenn nicht abziehbar), daher direkt summierbar.
 *
 * Total (wirft nie). Alle Ergebnis-Cents sind Integer (reine Integer-Summen).
 *
 * @param expenses Bereits geladenes Ausgaben-Array (dasselbe wie Cockpit/Tabelle).
 * @param period   Aufgelöster Zeitraum.
 * @param options  Einbezugsregeln (Default bookedOnly).
 * @returns VatInputAggregate (Eingangsseite der UStVA).
 */
export function toVatInputAggregate(
  expenses: Expense[],
  period: SummaryPeriod,
  options?: ToVatInputAggregateOptions,
): VatInputAggregate {
  const bookedOnly = options?.bookedOnly ?? true;

  let vatDeductibleStandardCents = 0;
  let reverseChargeOwedCents = 0;
  let reverseChargeNetCents = 0;
  let reverseChargeDeductibleCents = 0;
  let intraCommunityAcqVatCents = 0;
  let intraCommunityAcqNetCents = 0;
  let intraCommunityAcqDeductibleCents = 0;
  let excludedDraftVatOwedCount = 0;
  // VETO-1 (§7.4) — eingangsseitiger Bucket (booked-only, vorzeichen-aware).
  let unclassifiedExpenseGrossCents = 0;
  let unclassifiedExpenseCount = 0;

  for (const e of expenses ?? []) {
    if (!isWithinPeriod(e.expenseDate, period)) continue;

    // A1: draft-Schulden in der Periode sichtbar machen (vor dem booked-Gate) — fail-open-Schutz.
    if (e.status === 'draft') {
      const carriesOwed = e.vatOwedCents !== 0;
      const unconfirmedReverseCharge =
        REVERSE_CHARGE_SCHEMES.has(e.taxScheme) && e.taxSchemeConfirmedByUser === false;
      if (carriesOwed || unconfirmedReverseCharge) {
        excludedDraftVatOwedCount += 1;
      }
    }

    // Offizielle Summen: nur einbezogene Ausgaben (fail-closed: booked).
    if (bookedOnly && e.status !== 'booked') continue;

    switch (e.taxScheme) {
      case 'standard':
        vatDeductibleStandardCents += e.vatDeductibleCents; // KZ 66
        break;
      case 'reverse_charge_13b':
        reverseChargeNetCents += e.netCents;
        reverseChargeOwedCents += e.vatOwedCents;            // KZ 84/85
        reverseChargeDeductibleCents += e.vatDeductibleCents; // KZ 67 (getrennt!)
        break;
      case 'intra_community_6a':
        intraCommunityAcqNetCents += e.netCents;
        intraCommunityAcqVatCents += e.vatOwedCents;          // KZ 89/93
        intraCommunityAcqDeductibleCents += e.vatDeductibleCents; // KZ 61
        break;

      // ── VETO-1 (§7.4): eingangsseitig unklassifizierte Schemata ──────────────
      // Diese Schemata tragen NICHTS zu den UStVA-Summen bei (Kz62/EUSt out of scope,
      // needs_review = unklassifiziert). Kriterium ist der BUCHUNGSBETRAG, NICHT die
      // USt-Felder: `expenseTax` nullt `vatOwedCents`/`vatDeductibleCents` für diese
      // Schemata bereits AN DER QUELLE (SP-3) — ein USt-Feld-Kriterium wäre für exakt
      // den Ziel-Fall (Drittland-Import) tot. `grossCents` vorzeichen-aware (Storno).
      case 'import_third_country':
        if (e.netCents !== 0 || e.grossCents !== 0) {
          unclassifiedExpenseGrossCents += e.grossCents;
          unclassifiedExpenseCount += 1;
        }
        break;
      case 'needs_review':
        if (
          e.netCents !== 0 ||
          e.grossCents !== 0 ||
          e.vatOwedCents !== 0 ||
          e.vatDeductibleCents !== 0
        ) {
          unclassifiedExpenseGrossCents += e.grossCents;
          unclassifiedExpenseCount += 1;
        }
        break;

      // Legitime 0-Beiträge zur UStVA — NUR bei USt-Feld-Inkonsistenz zählen, sonst
      // blockte JEDE steuerfreie/§19-Eingangsausgabe den Export dauerhaft (§7.4).
      case 'zero_exempt':
      case 'small_business_19':
        if (e.vatOwedCents !== 0 || e.vatDeductibleCents !== 0) {
          unclassifiedExpenseGrossCents += e.grossCents;
          unclassifiedExpenseCount += 1;
        }
        break;

      // Fail-closed gegen künftige Enum-Erweiterungen: unbekanntes Schema mit
      // irgendeinem Betrag ≠ 0 landet im Bucket.
      default:
        if (
          e.netCents !== 0 ||
          e.grossCents !== 0 ||
          e.vatOwedCents !== 0 ||
          e.vatDeductibleCents !== 0
        ) {
          unclassifiedExpenseGrossCents += e.grossCents;
          unclassifiedExpenseCount += 1;
        }
        break;
    }
  }

  return {
    vatDeductibleStandardCents,
    reverseChargeOwedCents,
    reverseChargeNetCents,
    reverseChargeDeductibleCents,
    intraCommunityAcqVatCents,
    intraCommunityAcqNetCents,
    intraCommunityAcqDeductibleCents,
    excludedDraftVatOwedCount,
    unclassifiedExpenseGrossCents,
    unclassifiedExpenseCount,
  };
}
